import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { fmtHours, fmtDuration } from '../lib/formatters';
import { useAuth } from '../hooks/useAuth';
import { useSelectedUser } from '../hooks/useSelectedUser';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Mouse,
  Keyboard,
  Activity,
  Clock,
  Loader2,
  Zap,
  PauseCircle,
  Monitor,
} from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtTime(iso, tz = 'UTC', timeFormat = '12h') {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz, hour12: timeFormat === '12h' });
}

function dayLabel(dateStr, tz = 'UTC') {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', timeZone: tz });
}

function weekRange(weekStart, tz = 'UTC') {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const opts = { month: 'short', day: 'numeric', timeZone: tz };
  return `${weekStart.toLocaleDateString([], opts)} – ${end.toLocaleDateString([], opts)}`;
}

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function activityColor(pct) {
  if (pct >= 75) return 'bg-green-500';
  if (pct >= 40) return 'bg-yellow-500';
  if (pct > 0) return 'bg-red-400';
  return 'bg-gray-300';
}

function scoreColor(score) {
  if (score >= 75) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-500';
}

function scoreRingColor(score) {
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#ef4444';
}

// ─── Productivity Score Card ──────────────────────────────────────────────────

function ProductivityScoreCard({ userId, from, to }) {
  const { data, isLoading } = useQuery({
    queryKey: ['productivity-score', userId, from, to],
    queryFn: async () => {
      const res = await api.get(`/api/users/${userId}/productivity-score?from=${from}&to=${to}`);
      return res.data?.data;
    },
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!data || data.score === 0) return null;

  const { score, breakdown } = data;
  const circumference = 2 * Math.PI * 28;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Weekly Productivity Score</h3>
      <div className="flex items-center gap-6">
        {/* Circular score */}
        <div className="relative flex-shrink-0">
          <svg width="72" height="72" viewBox="0 0 72 72">
            <circle cx="36" cy="36" r="28" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle
              cx="36" cy="36" r="28"
              fill="none"
              stroke={scoreRingColor(score)}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 36 36)"
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-xl font-bold leading-none ${scoreColor(score)}`}>{score}</span>
            <span className="text-[9px] text-gray-400 mt-0.5">/ 100</span>
          </div>
        </div>

        {/* Breakdown bars */}
        <div className="flex-1 space-y-2.5">
          {[
            { label: 'Activity Ratio', value: breakdown.activityRatio, color: 'bg-blue-500' },
            { label: 'Focus Ratio',    value: breakdown.focusRatio,    color: 'bg-purple-500' },
            { label: 'Consistency',    value: breakdown.consistency,   color: 'bg-green-500' },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{label}</span>
                <span>{Math.round(value * 100)}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${color} rounded-full transition-all duration-700`}
                  style={{ width: `${Math.round(value * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Mini Timeline Bar ────────────────────────────────────────────────────────

function MiniTimeline({ segments, durationSeconds }) {
  if (!segments || segments.length === 0) return null;

  const totalMinutes = Math.max(1, Math.ceil(durationSeconds / 60));

  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 mt-2 ml-5" title="Session timeline">
      {segments.map((seg, i) => {
        const widthPct = (1 / totalMinutes) * 100;
        const color =
          seg.type === 'idle'  ? 'bg-gray-300' :
          seg.type === 'focus' ? 'bg-purple-500' :
                                 'bg-blue-400';
        return (
          <div
            key={i}
            className={`${color} flex-shrink-0`}
            style={{ width: `${widthPct}%` }}
            title={`${seg.type} — ${Math.round(seg.activityPct)}%`}
          />
        );
      })}
    </div>
  );
}

// ─── Screenshot Detail Modal ─────────────────────────────────────────────────

function ScreenshotModal({ screenshot, screenshots = [], currentIndex = 0, onClose, onNavigate, tz = 'UTC', timeFormat = '12h' }) {
  if (!screenshot) return null;

  const [localIndex, setLocalIndex] = useState(currentIndex);
  const onCloseRef = useRef(onClose);
  const onNavigateRef = useRef(onNavigate);
  const localIndexRef = useRef(localIndex);

  // Keep refs in sync with props
  useEffect(() => {
    onCloseRef.current = onClose;
    onNavigateRef.current = onNavigate;
    localIndexRef.current = localIndex;
  }, [onClose, onNavigate, localIndex]);

  // Update local index when currentIndex prop changes
  useEffect(() => {
    setLocalIndex(currentIndex);
  }, [currentIndex]);

  const current = screenshots[localIndex] || screenshot;

  const handlePrev = () => {
    if (localIndexRef.current > 0) {
      const newIndex = localIndexRef.current - 1;
      setLocalIndex(newIndex);
      onNavigateRef.current?.(newIndex);
    }
  };

  const handleNext = () => {
    if (localIndexRef.current < screenshots.length - 1) {
      const newIndex = localIndexRef.current + 1;
      setLocalIndex(newIndex);
      onNavigateRef.current?.(newIndex);
    }
  };

  // Keyboard navigation - stable deps with refs
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-h-[95vh] overflow-auto flex flex-col"
        style={{ maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {screenshots.length > 0 ? `${localIndex + 1} / ${screenshots.length}` : ''}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Image container with navigation */}
        <div className="relative flex-1 flex items-center justify-center bg-gray-900 min-h-[400px]">
          <img
            src={current.imageUrl}
            alt="Screenshot"
            className="max-w-full max-h-[80vh] object-contain"
          />

          {/* Prev button */}
          {localIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-90 text-gray-900 rounded-full hover:bg-opacity-100 transition-all hover:scale-110 shadow-lg"
              title="Previous (←)"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Next button */}
          {localIndex < screenshots.length - 1 && (
            <button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-90 text-gray-900 rounded-full hover:bg-opacity-100 transition-all hover:scale-110 shadow-lg"
              title="Next (→)"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Info section */}
        <div className="bg-white border-t border-gray-200 p-5">
          {current.windowTitle && (
            <div className="flex items-center gap-2 mb-4">
              <Monitor className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <p className="text-sm text-gray-700 truncate" title={current.windowTitle}>
                {current.windowTitle}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Time</p>
                <p className="text-sm font-semibold">{fmtTime(current.takenAt, tz, timeFormat)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Keyboard className="w-4 h-4 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">Keystrokes</p>
                <p className="text-sm font-semibold">{current.keyStrokes ?? 0}/min</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Mouse className="w-4 h-4 text-purple-500" />
              <div>
                <p className="text-xs text-gray-500">Mouse Clicks</p>
                <p className="text-sm font-semibold">{current.mouseClicks ?? 0}/min</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Activity</p>
                <p className="text-sm font-semibold">{Math.round(current.activityPct ?? 0)}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Day Detail View ─────────────────────────────────────────────────────────

function DayDetail({ day, onBack, tz = 'UTC', timeFormat = '12h' }) {
  const [selectedSS, setSelectedSS] = useState(null);
  const [allScreenshots, setAllScreenshots] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const totalIdle = day.sessions.reduce((s, sess) => s + (sess.idleMinutes || 0), 0);
  const totalFocus = day.sessions.reduce((s, sess) => s + (sess.focusMinutes || 0), 0);

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <h2 className="text-xl font-bold text-gray-900 mb-1">{dayLabel(day.date, tz)}</h2>

      <div className="flex flex-wrap items-center gap-6 mb-6 p-4 bg-gray-50 rounded-xl">
        <div>
          <p className="text-2xl font-bold text-gray-900">Total: {fmtDuration(day.totalSeconds)}</p>
        </div>
        {totalFocus > 0 && (
          <div className="flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-purple-700 font-medium">{totalFocus} min focus</span>
          </div>
        )}
        {totalIdle > 0 && (
          <div className="flex items-center gap-1.5">
            <PauseCircle className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">{totalIdle} min idle</span>
          </div>
        )}
      </div>

      {day.sessions.map((session) => (
        <div key={session.id} className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
            <span className="text-sm font-semibold text-gray-900">
              {fmtTime(session.startTime, tz, timeFormat)} – {session.endTime ? fmtTime(session.endTime, tz, timeFormat) : 'In progress'}
              {session.duration > 0 && (
                <span className="text-gray-500 font-normal"> ({fmtDuration(session.duration)})</span>
              )}
            </span>
            {/* Focus / Idle badges */}
            {session.focusMinutes >= 25 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-medium">
                <Zap className="w-3 h-3" /> {session.focusMinutes} min focus
              </span>
            )}
            {session.idleMinutes > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-medium">
                <PauseCircle className="w-3 h-3" /> {session.idleMinutes} min idle
              </span>
            )}
          </div>

          <p className="text-xs text-gray-500 mb-1 ml-5">
            {session.project}{session.task ? ` · ${session.task}` : ''}
            {session.avgActivity > 0 && (
              <span className="ml-3">
                Activity: {session.avgActivity}%  ·  🖱 {session.avgMouse}/min  ·  ⌨ {session.avgKeys}/min
              </span>
            )}
          </p>

          {/* Mini timeline bar */}
          <MiniTimeline segments={session.timelineSegments} durationSeconds={session.duration} />

          {/* Timeline legend (only if we have segments) */}
          {session.timelineSegments?.length > 0 && (
            <div className="flex items-center gap-3 ml-5 mt-1 mb-3">
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <span className="w-2 h-2 rounded-sm bg-purple-500 inline-block" /> Focus
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <span className="w-2 h-2 rounded-sm bg-blue-400 inline-block" /> Active
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-400">
                <span className="w-2 h-2 rounded-sm bg-gray-300 inline-block" /> Idle
              </span>
            </div>
          )}

          {session.screenshots.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 ml-5">
              {session.screenshots.map((ss, idx) => (
                <div
                  key={ss.id}
                  onClick={() => {
                    // Flatten all screenshots across all sessions for navigation
                    const allScreenshots = day.sessions.flatMap(s => s.screenshots);
                    const globalIndex = allScreenshots.findIndex(s => s.id === ss.id);
                    setSelectedSS(ss);
                    setAllScreenshots(allScreenshots);
                    setSelectedIndex(globalIndex);
                  }}
                  className="cursor-pointer rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all group"
                >
                  <div className="aspect-video bg-gray-100 relative">
                    <img
                      src={ss.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all" />
                  </div>
                  {/* Activity bar */}
                  <div className="h-1.5 bg-gray-200">
                    <div
                      className={`h-full ${activityColor(ss.activityPct || 0)}`}
                      style={{ width: `${Math.min(100, ss.activityPct || 0)}%` }}
                    />
                  </div>
                  <div className="px-2 py-1 text-center">
                    <span className="text-[10px] text-gray-500">{fmtTime(ss.takenAt, tz, timeFormat)}</span>
                    {ss.windowTitle && (
                      <p className="text-[9px] text-gray-400 truncate mt-0.5" title={ss.windowTitle}>
                        {ss.windowTitle}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 ml-5 mt-2">No screenshots for this session</p>
          )}
        </div>
      ))}

      <ScreenshotModal
        screenshot={selectedSS}
        screenshots={allScreenshots}
        currentIndex={selectedIndex}
        onClose={() => setSelectedSS(null)}
        onNavigate={(newIndex) => {
          setSelectedIndex(newIndex);
          setSelectedSS(allScreenshots[newIndex]);
        }}
        tz={tz}
        timeFormat={timeFormat}
      />
    </div>
  );
}

// ─── Main Work Diary Page ────────────────────────────────────────────────────

export default function WorkDiary() {
  const { user, isAdmin, isManager } = useAuth();
  const { timezone: tz, timeFormat } = useDisplaySettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [, setSelectedUserId] = useSelectedUser();
  const [selectedDay, setSelectedDay] = useState(null);

  // URL is the source of truth — read userId directly from params
  const selectedUserId = searchParams.get('userId') || '';

  // On mount: if no ?userId= in URL, default to own ID for employees
  useEffect(() => {
    if (!searchParams.get('userId') && !isAdmin && !isManager && user?.id) {
      setSearchParams({ userId: user.id }, { replace: true });
    }
  }, [user?.id]);

  const from = useMemo(() => {
    return new Date(weekStart).toLocaleDateString('en-CA', { timeZone: tz });
  }, [weekStart, tz]);

  const to = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d.toLocaleDateString('en-CA', { timeZone: tz });
  }, [weekStart, tz]);

  // Load all users for the chip picker (admin/manager only)
  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
    enabled: isManager,
    refetchInterval: 30000,
  });

  // Load live dashboard data to get today's hours + activity per user
  const { data: dashData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get('/api/dashboard');
      return res.data;
    },
    enabled: isManager,
    refetchInterval: 30000,
  });

  const { data: diaryData, isLoading } = useQuery({
    queryKey: ['work-diary', selectedUserId, from, to, tz],
    queryFn: async () => {
      const res = await api.get(`/api/users/${selectedUserId}/work-diary?from=${from}&to=${to}&tz=${encodeURIComponent(tz)}`);
      return res.data?.data;
    },
    enabled: !!selectedUserId,
  });

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      // Use timezone-aware date string so label and data key always match
      const dateStr = d.toLocaleDateString('en-CA', { timeZone: tz }); // en-CA gives YYYY-MM-DD
      const dayData = diaryData?.days?.find((dy) => dy.date === dateStr);
      days.push({
        date: dateStr,
        dayName: d.toLocaleDateString([], { weekday: 'long', timeZone: tz }),
        dayNum: d.toLocaleDateString([], { day: 'numeric', timeZone: tz }),
        totalSeconds: dayData?.totalSeconds || 0,
        sessions: dayData?.sessions || [],
      });
    }
    return days;
  }, [weekStart, diaryData, tz]);

  const maxDaySeconds = Math.max(1, ...weekDays.map((d) => d.totalSeconds));

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
    setSelectedDay(null);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
    setSelectedDay(null);
  };

  if (selectedDay) {
    return (
      <div className="max-w-6xl mx-auto">
        <DayDetail day={selectedDay} onBack={() => setSelectedDay(null)} tz={tz} timeFormat={timeFormat} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Work Diary</h1>
      </div>

      {/* Employee chip picker — admin/manager only */}
      {isManager && usersData && usersData.length > 0 && (() => {
        const activeTrackers = dashData?.activeTrackers || [];
        const trackerMap = Object.fromEntries(activeTrackers.map(t => [t.userId, t]));
        return (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {usersData.map((u) => {
              const tracker = trackerMap[u.id];
              const isSelected = u.id === selectedUserId;
              const isOnline = u.isOnline || !!tracker;
              const hoursToday = tracker
                ? tracker.hoursToday
                : null;
              const activity = tracker?.activityLevel ?? null;

              return (
                <button
                  key={u.id}
                  onClick={() => { setSearchParams({ userId: u.id }, { replace: true }); setSelectedDay(null); }}
                  className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {/* Status dot */}
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />

                  {/* Avatar */}
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    isSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {u.name.charAt(0).toUpperCase()}
                  </span>

                  <span className="font-medium whitespace-nowrap">{u.name.split(' ')[0]}</span>

                  {/* Today's hours + activity if tracking */}
                  {hoursToday != null && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {fmtHours(hoursToday)}
                    </span>
                  )}
                  {activity != null && (
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      activity >= 75 ? 'bg-green-100 text-green-700' :
                      activity >= 40 ? 'bg-yellow-100 text-yellow-700' :
                                       'bg-red-100 text-red-600'
                    }`}>
                      {Math.round(activity)}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Week navigation */}
      <div className="flex items-center gap-4">
        <button
          onClick={prevWeek}
          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-lg font-semibold text-gray-800">{weekRange(weekStart, tz)}</span>
        <button
          onClick={nextWeek}
          className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekly bars */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {weekDays.map((day) => (
            <div
              key={day.date}
              onClick={() => day.totalSeconds > 0 && setSelectedDay(day)}
              className={`flex items-center gap-4 px-5 py-3.5 ${
                day.totalSeconds > 0
                  ? 'cursor-pointer hover:bg-gray-50 transition-colors'
                  : 'opacity-60'
              }`}
            >
              <span className="text-sm font-medium text-gray-700 w-32">
                {day.dayNum} {day.dayName}
              </span>
              <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                {day.totalSeconds > 0 && (
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max(2, (day.totalSeconds / maxDaySeconds) * 100)}%`,
                    }}
                  />
                )}
              </div>
              <span className="text-sm font-semibold text-gray-800 w-20 text-right">
                {day.totalSeconds > 0 ? fmtDuration(day.totalSeconds) : '0m'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Totals + Productivity Score */}
      {diaryData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Totals */}
          <div className="flex flex-wrap items-center gap-8 p-5 bg-white rounded-xl border border-gray-200">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
              <div>
                <span className="text-xs text-gray-500">Tracked</span>
                <p className="text-lg font-bold">{fmtDuration(diaryData.totalSeconds)}</p>
              </div>
            </div>
            {Number(diaryData.hourlyRate) > 0 && (
              <div>
                <span className="text-xs text-gray-500">Total amount</span>
                <p className="text-lg font-bold">${diaryData.totalAmount}</p>
                <p className="text-xs text-gray-400">
                  {fmtDuration(diaryData.totalSeconds)} @ ${diaryData.hourlyRate}/hr
                </p>
              </div>
            )}
          </div>

          {/* Productivity score card */}
          {selectedUserId && (
            <ProductivityScoreCard userId={selectedUserId} from={from} to={to} />
          )}
        </div>
      )}
    </div>
  );
}
